import { Link } from "react-router-dom";
import TeamLogo from "@/components/base/TeamLogo";
import { getTeam, formatDateCN, statusMap } from "@/utils";
import type { Match } from "@/types";

interface MatchCardProps {
  match: Match;
  variant?: "card" | "row";
}

function StatusBadge({ status }: { status: Match["status"] }) {
  if (status === "live") {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-accent-500 text-accent-950 text-xs font-label font-semibold">
        <span className="w-1.5 h-1.5 rounded-full bg-accent-950 animate-pulse-dot"></span>
        {statusMap.live.label}
      </span>
    );
  }
  if (status === "upcoming") {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-800 text-xs font-label font-medium">
        {statusMap.upcoming.label}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-background-200 text-foreground-600 text-xs font-label font-medium">
      {statusMap.finished.label}
    </span>
  );
}

export default function MatchCard({ match, variant = "card" }: MatchCardProps) {
  const home = getTeam(match.home);
  const away = getTeam(match.away);
  const isLive = match.status === "live";
  const hasScore = match.status !== "upcoming";

  if (variant === "row") {
    return (
      <Link
        to={`/match/${match.id}`}
        className="group grid grid-cols-1 sm:grid-cols-12 items-center gap-3 sm:gap-4 px-4 py-4 bg-background-50 rounded-lg border border-background-200 hover:border-primary-300 transition-colors cursor-pointer"
      >
        <div className="sm:col-span-3 flex items-center gap-3">
          <span className="font-label text-sm font-semibold text-foreground-900 w-12">{match.time}</span>
          <span className="text-xs text-foreground-500">{formatDateCN(match.date)}</span>
        </div>
        <div className="sm:col-span-5 flex items-center gap-3">
          <TeamLogo teamId={match.home} size="sm" />
          <span className="text-sm font-medium text-foreground-900">{home.short}</span>
          <span className="text-xs text-foreground-400 font-label">VS</span>
          <TeamLogo teamId={match.away} size="sm" />
          <span className="text-sm font-medium text-foreground-900">{away.short}</span>
        </div>
        <div className="sm:col-span-2">
          <StatusBadge status={match.status} />
        </div>
        <div className="sm:col-span-2 flex items-center justify-between sm:justify-end gap-2">
          {hasScore ? (
            <span className="font-heading text-lg font-bold text-foreground-950">
              {match.homeScore} : {match.awayScore}
            </span>
          ) : (
            <span className="text-xs text-foreground-400">{match.venue}</span>
          )}
          <i className="ri-arrow-right-s-line text-xl text-foreground-300 group-hover:text-primary-500 transition-colors"></i>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/match/${match.id}`}
      className={`group block bg-background-50 rounded-lg border overflow-hidden hover:border-primary-300 transition-colors cursor-pointer ${
        isLive ? "border-accent-300" : "border-background-200"
      }`}
    >
      <div className="flex items-center justify-between px-4 py-3 bg-background-100 border-b border-background-200">
        <span className="text-xs text-foreground-600 font-label">{match.stage}</span>
        <StatusBadge status={match.status} />
      </div>
      <div className="px-4 py-5">
        <div className="flex items-center justify-between gap-2">
          <div className="flex flex-col items-center gap-2 w-20">
            <TeamLogo teamId={match.home} />
            <span className="text-xs font-medium text-foreground-900 text-center leading-tight">{home.short}</span>
          </div>
          <div className="flex flex-col items-center justify-center flex-1">
            {hasScore ? (
              <div className="flex items-baseline gap-2 font-heading font-bold">
                <span className={`text-3xl ${isLive ? "text-accent-600" : "text-foreground-950"}`}>{match.homeScore}</span>
                <span className="text-foreground-300 text-xl">:</span>
                <span className={`text-3xl ${isLive ? "text-accent-600" : "text-foreground-950"}`}>{match.awayScore}</span>
              </div>
            ) : (
              <span className="font-heading text-2xl font-bold text-foreground-400">VS</span>
            )}
            <span className="mt-2 text-xs text-foreground-500 font-label">
              {isLive ? `${match.period} ${match.clock}` : `${formatDateCN(match.date)} ${match.time}`}
            </span>
          </div>
          <div className="flex flex-col items-center gap-2 w-20">
            <TeamLogo teamId={match.away} />
            <span className="text-xs font-medium text-foreground-900 text-center leading-tight">{away.short}</span>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-between px-4 py-3 border-t border-background-200">
        <span className="text-xs text-foreground-500 truncate flex items-center gap-1">
          <i className="ri-map-pin-line"></i> {match.venue}
        </span>
        <span className="text-xs font-medium text-primary-600 group-hover:text-accent-600 flex items-center gap-1 whitespace-nowrap">
          {isLive ? "进入直播" : "查看详情"} <i className="ri-arrow-right-s-line"></i>
        </span>
      </div>
    </Link>
  );
}